import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAutoNavigation } from './use-auto-navigation';
import { useConfirmationDialog } from '../ui/use-confirmation-dialog';

/**
 * Workbook-specific navigation hook
 * Provides navigation patterns specific to workbook operations
 */
export function useWorkbookNavigation() {
  const { goToContentGenerator, redirectAfterAction, navigate } = useAutoNavigation();
  const queryClient = useQueryClient();
  const { showConfirmation, ConfirmationDialog } = useConfirmationDialog();

  // Function to invalidate all relevant queries - simplified to use only workbook-projects
  const invalidateAllQueries = useCallback(() => {
    // Invalidate workbook queries (used by Content Generator page)
    queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
    // Invalidate workbook count queries (used by sidebar)
    queryClient.invalidateQueries({ queryKey: ['workbook-count-all'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['workbook-count-pinned'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['workbook-count-archived'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['workbook-count-total-user'], exact: false });
  }, [queryClient]);

  // Delete workbook with immediate redirect (no waiting for completion)
  const deleteWorkbookWithImmediateRedirect = useCallback(async (
    deleteAction: () => Promise<void> | void,
    options?: {
      confirmMessage?: string;
    }
  ) => {
    const {
      confirmMessage = 'This action cannot be undone.',
    } = options || {};

    const confirmed = await showConfirmation({
      title: 'Delete Project',
      description: confirmMessage,
      confirmText: 'Delete Forever',
      cancelText: 'Cancel',
      confirmVariant: 'red',
      cancelVariant: 'green'
    });

    if (confirmed) {
      // Execute the delete action but don't wait for it
      deleteAction();
      // Immediately invalidate all queries to ensure fresh data
      invalidateAllQueries();
      // Redirect immediately to project manager after project deletion
      navigate('/project-manager');
    }
  }, [navigate, invalidateAllQueries, showConfirmation]);

  // Delete workbook with delayed redirect (original behavior)
  const deleteWorkbookWithRedirect = useCallback(async (
    deleteAction: () => Promise<void> | void,
    options?: {
      confirmMessage?: string;
      redirectDelay?: number;
    }
  ) => {
    const {
      confirmMessage = 'This action cannot be undone.',
      redirectDelay = 500
    } = options || {};

    const confirmed = await showConfirmation({
      title: 'Delete Project',
      description: confirmMessage,
      confirmText: 'Delete Forever',
      cancelText: 'Cancel',
      confirmVariant: 'red',
      cancelVariant: 'green'
    });

    if (confirmed) {
      return redirectAfterAction(
        deleteAction,
        '/project-manager',
        redirectDelay
      );
    }
  }, [redirectAfterAction, showConfirmation]);

  // Archive workbook with optional redirect
  const archiveWorkbookWithRedirect = useCallback((
    archiveAction: () => Promise<void> | void,
    shouldRedirect: boolean = false,
    options?: {
      redirectDelay?: number;
    }
  ) => {
    if (shouldRedirect) {
      return redirectAfterAction(
        archiveAction,
        '/project-manager',
        options?.redirectDelay || 1000
      );
    } else {
      return archiveAction();
    }
  }, [redirectAfterAction]);

  // Navigate to workbook editor with validation
  const openWorkbook = useCallback((workbookId: string) => {
    if (!workbookId) {
      console.error('Cannot open workbook: Invalid workbook ID');
      return;
    }
    navigate(`/project/${workbookId}/tasks`);
  }, [navigate]);

  // Return to content generator (common pattern)
  const returnToContentGenerator = useCallback(() => {
    goToContentGenerator();
  }, [goToContentGenerator]);

  return {
    // Workbook-specific operations
    deleteWorkbookWithImmediateRedirect,
    deleteWorkbookWithRedirect,
    archiveWorkbookWithRedirect,
    openWorkbook,
    returnToContentGenerator,
    
    // Re-export common navigation for convenience
    goToContentGenerator,
    navigate,
    
    // Confirmation dialog component - must be rendered in the component
    ConfirmationDialog
  };
} 